import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import { localize } from 'react-localize-redux';
import { colors } from 'helpers/ColorHelper';
import { Field, change, formValueSelector } from 'redux-form';
import * as validator from 'helpers/Validator';

import { getProperties, getUnits, getActiveLeaseOfUnit } from 'actions/landlordPropertiesActions';

import MultiSelect from './NoticeMultiselect';
import { LegalNotice, NoticeOfEntryNotice } from './constants';
import TenantResidentVerbiageHelper from 'components/shared/TenantResidentVerbiageHelper';
import { getTranslatedString } from 'helpers/i18n';

const FullWidthSelect = styled(MultiSelect)`
  width: 100%;
`;

const modes = {
  SELECTING_PROPERTY: 'SELECTING_PROPERTY',
  SELECTING_PROPERTY_OPTIONS: 'SELECTING_PROPERTY_OPTIONS',
  SELECTING_UNIT: 'SELECTING_UNIT',
  SELECTING_FLOORS: 'SELECTING_FLOORS',
  SELECTING_ROWS: 'SELECTING_ROWS',
  SELECTING_UNIT_COTENANTS: 'SELECTING_UNIT_COTENANTS',
};

const fieldStyle = { textAlign: 'left', fontSize: '14px' };
const labelStyle = {
  transform: 'scale(0.75) translateY(15px)',
  transformOrigin: 'left top 0px',
  color: colors.LABEL_GRAY,
  float: 'left',
  fontSize: '16px',
};
class NoticeAssignmentForm extends Component {
  constructor(props) {
    super(props);
    const { selectedProperties, selectedUnits, selectedCotenants, selectedFloors, selectedRows } =
      props;

    let propertyOption = 'directly_to_property';
    let unitOption = 'directly_to_unit';
    let mode = null;

    if (selectedProperties) {
      this.loadPropertyUnits();
    }

    if (selectedUnits) {
      this.loadUnitCotenants(selectedUnits[0]);
    }

    if (!selectedProperties || selectedProperties.length === 0) {
      mode = modes.SELECTING_PROPERTY;
    } else if (selectedProperties.length === 1 && (!selectedUnits || selectedUnits.length === 0)) {
      mode = modes.SELECTING_PROPERTY_OPTIONS;
    } else if (selectedUnits && selectedCotenants) {
      mode = modes.SELECTING_UNIT_COTENANTS;
      propertyOption = 'units_within_property';
      unitOption = 'to_unit_cotenants';
    } else if (selectedUnits) {
      propertyOption = 'units_within_property';
      mode = modes.SELECTING_UNIT;
    } else if (selectedFloors) {
      propertyOption = 'floors_within_property';
      mode = modes.SELECTING_FLOORS;
    } else if (selectedRows) {
      propertyOption = 'rows_within_property';
      mode = modes.SELECTING_ROWS;
    }

    this.state = {
      propertyOption: propertyOption,
      unitOption: unitOption,
      mode: mode,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (!!prevProps.noticeType && prevProps.noticeType !== this.props.noticeType) {
      this.resetPropertySelections();
    }
    // Reset assignment form to the start if properties are set to []
    if (this.state.mode === modes.SELECTING_PROPERTY) return;
    if (prevState.mode === modes.SELECTING_PROPERTY) return;
    if (Array.isArray(prevProps.selectedProperties) && prevProps.selectedProperties.length === 0)
      return;

    if (
      Array.isArray(this.props.selectedProperties) &&
      this.props.selectedProperties.length === 0
    ) {
      this.setState({
        mode: modes.SELECTING_PROPERTY,
      });
    }
  }

  getFilteredProperties = () => {
    const { properties, shouldFilterProperties, filteredProperties, isTemplate } = this.props;

    let propertyOptions = properties;

    // If this is a legal notice, only allow properties that have that flag set
    if (this.isLegalNotice()) {
      propertyOptions = propertyOptions.filter((p) => !!p.can_send_legal_notices);
    }

    // If we're not using a template, template isn't for specific properties or
    // we don't have a list of properties to allow selection from, return what we've got.
    if (!isTemplate || !shouldFilterProperties || filteredProperties.length === 0) {
      return propertyOptions;
    }

    const templatePropertyIds = filteredProperties.map((property) => property.value);
    return propertyOptions.filter((property) => templatePropertyIds.includes(property.value));
  };

  loadPropertyUnits = (property = null) => {
    const { selectedProperties } = this.props;
    if (property && property.value) {
      this.props.getUnits(property.value, { for_multiselect_with_floor_riser: true });
    } else if (Array.isArray(selectedProperties) && selectedProperties.length === 1) {
      const propertyId = this.props.selectedProperties[0].value;
      this.props.getUnits(propertyId, { for_multiselect_with_floor_riser: true });
    } else if (selectedProperties && selectedProperties.value) {
      const propertyId = this.props.selectedProperties.value;
      this.props.getUnits(propertyId, { for_multiselect_with_floor_riser: true });
    }
  };

  loadUnitCotenants = (unit = null) => {
    if (unit && unit.value) {
      this.props.getActiveLeaseOfUnit(unit.value);
    }
  };

  handleUnitChange = (unit) => {
    let selectedUnit = unit && unit[0];
    this.resetCotenantSelections();

    if ((this.isLegalNotice() || this.isNoticeOfEntryNotice()) && selectedUnit.value) {
      this.loadUnitCotenants(selectedUnit);
      this.setState({
        unitOption: 'to_unit_cotenants',
        mode: modes.SELECTING_UNIT_COTENANTS,
      });
    }
  };

  resetUnitSelections = () => {
    this.props.change(this.props.formName, 'units', []);
  };

  resetCotenantSelections = () => {
    this.props.change(this.props.formName, 'cotenants', []);
  };

  resetPropertySelections = () => {
    this.props.change(this.props.formName, 'properties', []);
  };

  handlePropertyOptionChange = (newValue) => {
    const { selectedProperties } = this.props;
    let newMode = modes.SELECTING_PROPERTY_OPTIONS;
    if (selectedProperties.length > 1) return null;
    if (newValue === 'units_within_property') {
      newMode = modes.SELECTING_UNIT;
      this.loadPropertyUnits();
    } else if (newValue === 'floors_within_property') {
      newMode = modes.SELECTING_FLOORS;
      this.loadPropertyUnits();
    } else if (newValue === 'rows_within_property') {
      newMode = modes.SELECTING_ROWS;
      this.loadPropertyUnits();
    }
    this.setState({
      propertyOption: newValue,
      mode: newMode,
    });

    this.resetUnitSelections();
    this.resetCotenantSelections();

    let selectedProperty = selectedProperties;
    if (Array.isArray(selectedProperties)) {
      selectedProperty = selectedProperties[0];
    }

    if (newValue === 'units_within_property' && selectedProperty.is_single_unit) {
      const unit = {
        value: selectedProperty.unit_id,
        label: `${selectedProperty.label} ${
          this.props.translate('landlordPortal.noticeAssignmentForm.unit').props.children
        }`,
      };
      this.props.change(this.props.formName, 'units', unit);
      this.handleUnitChange();
    }
  };

  handlePropertyChange = (property) => {
    const { templateDelivery } = this.props;

    this.resetUnitSelections();

    let selectedProperty = property && property[0];
    if (
      this.isLegalNotice() ||
      this.isNoticeOfEntryNotice() ||
      (templateDelivery === 'single_unit' && selectedProperty.value)
    ) {
      this.loadPropertyUnits(selectedProperty);
      this.setState({
        propertyOption: 'units_within_property',
        unitOption: 'directly_to_unit',
        mode: modes.SELECTING_UNIT,
      });
    } else {
      this.setState({
        propertyOption: 'directly_to_property',
        unitOption: 'directly_to_unit',
        mode: modes.SELECTING_PROPERTY_OPTIONS,
      });
    }
  };

  getPropertyOptionFields = () => {
    const { selectedProperties, properties, templateDelivery, translate } = this.props;

    // For legal and notice of entry notices, only sending to a single unit within
    // the property is possible.
    if (this.isLegalNotice() || this.isNoticeOfEntryNotice()) {
      return [
        {
          value: 'units_within_property',
          label: translate('landlordPortal.noticeAssignmentForm.unitWithinProperty').props.children,
        },
      ];
    }

    let propertyOptionFields = [];

    if (templateDelivery !== 'single_unit') {
      propertyOptionFields.push({
        value: 'directly_to_property',
        label: translate('landlordPortal.noticeAssignmentForm.assignToProperty').props.children,
      });
    }

    propertyOptionFields.push({
      value: 'units_within_property',
      label: translate('landlordPortal.noticeAssignmentForm.unitWithinProperty').props.children,
    });

    // If selected_properties is empty or contains more than 1 property just return!!!
    if (!selectedProperties) return propertyOptionFields;
    if (Array.isArray(selectedProperties) && selectedProperties.length !== 1)
      return propertyOptionFields;

    let selectedPropertySingle = selectedProperties;
    if (Array.isArray(selectedProperties)) {
      selectedPropertySingle = selectedProperties[0];
    }

    const selectedProperty = properties.find(
      (property) => property.value === selectedPropertySingle.value
    );

    if (selectedProperty.has_floors && templateDelivery !== 'single_unit') {
      propertyOptionFields.push({
        value: 'floors_within_property',
        label: translate('landlordPortal.noticeAssignmentForm.floorWithinProperty').props.children,
      });
    }

    if (selectedProperty.has_risers && templateDelivery !== 'single_unit') {
      propertyOptionFields.push({
        value: 'rows_within_property',
        label: translate('landlordPortal.noticeAssignmentForm.rowWithinProperty').props.children,
      });
    }

    return propertyOptionFields;
  };

  getCotenants = () => {
    const { unitActiveLease } = this.props;
    let cotenants = unitActiveLease.cotenants;
    if (this.isLegalNotice()) {
      cotenants = cotenants.filter((c) => !!c.digital_legal_documents);
    } else if (this.isNoticeOfEntryNotice()) {
      cotenants = cotenants.filter((c) => !!c.notice_by_email);
    }
    const options = cotenants.map((c) => ({ label: c.full_name, value: c.id }));
    return options;
  };

  getFloors = () => {
    const { units } = this.props;

    if (!units || units.length < 0) return [];

    let floorsHash = {};
    units.forEach((unit) => {
      if (unit['floor_name'] in floorsHash) {
        floorsHash[unit['floor_name']] += 1;
      } else if (unit['floor_name']) {
        floorsHash[unit['floor_name']] = 1;
      }
    });
    return Object.keys(floorsHash).map((key) => ({
      value: key,
      label: `${key} - ${floorsHash[key]} ${
        this.props.translate('landlordPortal.noticeAssignmentForm.unitsLowcase').props.children
      }`,
      number_of_units: floorsHash[key],
    }));
  };

  getRows = () => {
    const { units } = this.props;

    if (!units || units.length < 0) return [];

    let risersHash = {};
    units.forEach((unit) => {
      if (unit['riser_name'] in risersHash) {
        risersHash[unit['riser_name']] += 1;
      } else if (unit['riser_name']) {
        risersHash[unit['riser_name']] = 1;
      }
    });
    return Object.keys(risersHash).map((key) => ({
      value: key,
      label: `${key} - ${risersHash[key]} ${
        this.props.translate('landlordPortal.noticeAssignmentForm.unitsLowcase').props.children
      }`,
      number_of_units: risersHash[key],
    }));
  };

  render() {
    return (
      <div>
        {this.renderPropertiesSelect()}
        {this.renderPropertyOptionField()}
        {this.renderUnitSelectField()}
        {this.renderUnitCotenantsSelectField()}
        {this.renderFloorSelectField()}
        {this.renderRowSelectField()}
      </div>
    );
  }

  isLegalNotice = () => {
    return this.props.noticeType === LegalNotice;
  };

  isNoticeOfEntryNotice = () => {
    return this.props.noticeType === NoticeOfEntryNotice;
  };

  isMultiPropertySelect = () => {
    const { templateDelivery } = this.props;

    // Legal notices can only be sent to a unit within a single property
    if (this.isLegalNotice() || this.isNoticeOfEntryNotice()) {
      return false;
    }

    // Single property selection if the template provided is configured for a single
    // property and unit - multiselect otherwise
    return templateDelivery !== 'single_property' && templateDelivery !== 'single_unit';
  };

  renderPropertiesSelect = () => {
    const { readOnly, translate } = this.props;

    const multiPropertySelect = this.isMultiPropertySelect();

    return (
      <div className="row">
        <div className="col-xs-12">
          <label style={labelStyle}>
            {this.props.translate('landlordPortal.noticeAssignmentForm.properties')}
          </label>
        </div>
        <div className="col-xs-12">
          <Field
            name="properties"
            reduxFormName="properties"
            options={this.getFilteredProperties()}
            component={FullWidthSelect}
            onChange={this.handlePropertyChange}
            multiple={multiPropertySelect}
            label={translate('landlordPortal.noticeAssignmentForm.properties').props.children}
            fullWidth
            withResetSelectAllButtons
            validate={[validator.required]}
            disabled={readOnly}
          />
        </div>
      </div>
    );
  };

  renderPropertyOptionField = () => {
    const { mode: currentMode, propertyOption } = this.state;
    const { selectedProperties, readOnly, templateDelivery, translate } = this.props;

    const multiplePropertiesSelected =
      Array.isArray(selectedProperties) && selectedProperties.length !== 1;
    if (currentMode === modes.SELECTING_PROPERTY || multiplePropertiesSelected) {
      return null;
    }

    const disabled =
      readOnly ||
      templateDelivery === 'single_unit' ||
      this.isLegalNotice() ||
      this.isNoticeOfEntryNotice();

    return (
      <div className="row">
        <div className="col-xs-12">
          <label style={labelStyle}>
            {translate('landlordPortal.noticeAssignmentForm.propertyOptions')}
          </label>
          <FullWidthSelect
            fullWidth
            value={propertyOption}
            onChange={this.handlePropertyOptionChange}
            options={this.getPropertyOptionFields()}
            style={fieldStyle}
            disabled={disabled}
          />
        </div>
      </div>
    );
  };

  isMultiUnitSelect = () => {
    const { templateDelivery } = this.props;

    // Legal and notice of entry notices only go to 1 unit
    if (this.isLegalNotice() || this.isNoticeOfEntryNotice()) {
      return false;
    }

    // Single select if the template is configured for a single unit,
    // multi-select otherwise.
    return templateDelivery !== 'single_unit';
  };

  renderUnitSelectField = () => {
    const currentMode = this.state.mode;
    const { units, selectedProperties, readOnly, translate } = this.props;

    const multiUnitSelect = this.isMultiUnitSelect();

    if (isEmpty(selectedProperties)) return null;
    if (Array.isArray(selectedProperties) && selectedProperties.length > 1) return null;
    if (!Array.isArray(selectedProperties) && !selectedProperties.value) return null;
    if (![modes.SELECTING_UNIT, modes.SELECTING_UNIT_COTENANTS].includes(currentMode)) return null;

    let selectedProperty = selectedProperties;
    if (Array.isArray(selectedProperties) && selectedProperties.length === 1) {
      selectedProperty = selectedProperties[0];
    }

    // TODO - work out what to do here for LegalNotice
    if (selectedProperty.is_single_unit) return null;

    return (
      <div className="row">
        <div className="col-xs-12">
          <label style={labelStyle}>{translate('landlordPortal.noticeAssignmentForm.units')}</label>
        </div>
        <div className="col-xs-12">
          <Field
            name="units"
            reduxFormName="units"
            options={units}
            onChange={this.handleUnitChange}
            component={FullWidthSelect}
            multiple={multiUnitSelect}
            label={translate('landlordPortal.noticeAssignmentForm.unitsPlural').props.children}
            fullWidth
            withResetSelectAllButtons
            validate={[validator.required]}
            disabled={readOnly}
          />
        </div>
      </div>
    );
  };

  renderUnitCotenantsSelectField = () => {
    const currentMode = this.state.mode;
    const { unitActiveLease, selectedProperties, readOnly } = this.props;

    if (!unitActiveLease) return null;
    if (isEmpty(selectedProperties)) return null;
    if (Array.isArray(selectedProperties) && selectedProperties.length > 1) return null;
    if (!Array.isArray(selectedProperties) && !selectedProperties.value) return null;
    if (currentMode !== modes.SELECTING_UNIT_COTENANTS) return null;

    const cotenantOptions = this.getCotenants();

    const labelVerbiage = (
      <TenantResidentVerbiageHelper
        translatedTenant={getTranslatedString(
          'landlordPortal.noticeAssignmentForm.cotenantsTenant'
        )}
        translatedResident={getTranslatedString(
          'landlordPortal.noticeAssignmentForm.cotenantsResident'
        )}
      />
    );

    const fieldLabelVerbiage = (
      <TenantResidentVerbiageHelper
        translatedTenant={getTranslatedString(
          'landlordPortal.noticeAssignmentForm.cotenantsTenantPlural'
        )}
        translatedResident={getTranslatedString(
          'landlordPortal.noticeAssignmentForm.cotenantsResidentPlural'
        )}
      />
    );

    return (
      <div className="row">
        <div className="col-xs-12">
          <label style={labelStyle}>{labelVerbiage}</label>
        </div>
        <div className="col-xs-12">
          <Field
            name="cotenants"
            reduxFormName="cotenants"
            options={cotenantOptions}
            component={FullWidthSelect}
            multiple={true}
            label={fieldLabelVerbiage}
            fullWidth
            withResetSelectAllButtons
            validate={[validator.required]}
            disabled={readOnly}
          />
        </div>
      </div>
    );
  };

  renderFloorSelectField = () => {
    const currentMode = this.state.mode;
    const { selectedProperties, translate } = this.props;
    if (isEmpty(selectedProperties)) return null;
    if (Array.isArray(selectedProperties) && selectedProperties.length > 1) return null;
    if (!Array.isArray(selectedProperties) && !selectedProperties.value) return null;
    if (currentMode !== modes.SELECTING_FLOORS) return null;

    let selectedProperty = selectedProperties;
    if (Array.isArray(selectedProperties) && selectedProperties.length === 1) {
      selectedProperty = selectedProperties[0];
    }
    if (selectedProperty.is_single_unit) return null;
    return (
      <div className="row">
        <div className="col-xs-12">
          <label style={labelStyle}>
            {translate('landlordPortal.noticeAssignmentForm.floors')}
          </label>
        </div>
        <div className="col-xs-12">
          <Field
            name="floors"
            reduxFormName="floors"
            options={this.getFloors()}
            component={FullWidthSelect}
            multiple
            label={translate('landlordPortal.noticeAssignmentForm.floorsPlural').props.children}
            fullWidth
            withResetSelectAllButtons
            validate={[validator.required]}
            disabled={this.props.readOnly}
          />
        </div>
      </div>
    );
  };

  renderRowSelectField = () => {
    const currentMode = this.state.mode;
    const { selectedProperties } = this.props;
    if (isEmpty(selectedProperties)) return null;
    if (Array.isArray(selectedProperties) && selectedProperties.length > 1) return null;
    if (!Array.isArray(selectedProperties) && !selectedProperties.value) return null;
    if (currentMode !== modes.SELECTING_ROWS) return null;

    let selectedProperty = selectedProperties;
    if (Array.isArray(selectedProperties) && selectedProperties.length === 1) {
      selectedProperty = selectedProperties[0];
    }
    if (selectedProperty.is_single_unit) return null;
    return (
      <div className="row">
        <div className="col-xs-12">
          <label style={labelStyle}>
            {this.props.translate('landlordPortal.noticeAssignmentForm.rows')}
          </label>
        </div>
        <div className="col-xs-12">
          <Field
            name="risers"
            reduxFormName="risers"
            options={this.getRows()}
            component={FullWidthSelect}
            multiple
            label={
              this.props.translate('landlordPortal.noticeAssignmentForm.rowsPlural').props.children
            }
            fullWidth
            withResetSelectAllButtons
            validate={[validator.required]}
            disabled={this.props.readOnly}
          />
        </div>
      </div>
    );
  };
}

const mapStoreToProps = (store, ownProps) => {
  return {
    properties: store.landlordProperties.properties,
    units: store.landlordProperties.units,
    unitActiveLease: store.landlordProperties.unitActiveLease,
    assets: store.maintenanceReducer.maintenanceAssets,
    rooms: store.landlordProperties.rooms,
    selectedProperties: formValueSelector(ownProps.formName)(store, 'properties'),
    selectedUnits: formValueSelector(ownProps.formName)(store, 'units'),
    selectedCotenants: formValueSelector(ownProps.formName)(store, 'cotenants'),
    selectedFloors: formValueSelector(ownProps.formName)(store, 'floors'),
    selectedRows: formValueSelector(ownProps.formName)(store, 'risers'),
  };
};

const mapDispatchToProps = {
  getUnits,
  getActiveLeaseOfUnit,
  getProperties,
  change,
};

export default connect(mapStoreToProps, mapDispatchToProps)(localize()(NoticeAssignmentForm));
